import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { FundSettingsChangeDtoOut } from '@/models/out/FundSettingsChangeDtoOut';
import { formatDateLocale, formatDateTimeLocale } from '@/helpers/dateHelper';
import { describeWorlds, parseWorldsRecord } from '@/helpers/permissionWorlds';
import arrowDownIcon from '@/assets/icons/arrowDown.svg';
import styles from '../FundSettings.module.css';

/**
 * HAT-1231: «היסטוריית שינויים» - who changed what in this fund's settings, and when.
 *
 * Not a panel of its own (review, note 25): it grows with time and is empty for months on most
 * funds, so it is one line at the foot of the people panel - «היסטוריית שינויים (N) · שינוי אחרון
 * {date}» - that opens into a plain chronological list. Actors are shown by name (the office's
 * changes as «המשרד»), with the address as a tooltip.
 */
const SettingsHistorySection = ({ fundId }: { fundId: number }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { data: changes, isLoading } = useGetAuthApi<FundSettingsChangeDtoOut[]>(
    `funds/financial-data/${fundId}/settings-history`
  );

  const permissionLabel = (level?: string) => {
    // A mix of permission worlds is recorded as "Worlds:Edit,View,None" (22.10.2026).
    const worlds = parseWorldsRecord(level);
    if (worlds) return t('fundSettingsHistory.customPermission', { worlds: describeWorlds(worlds, t) });
    return level === 'ViewOnly'
      ? t('fundSettingsRepresentativesPage.permissionViewOnly')
      : t('fundSettingsRepresentativesPage.permissionFull');
  };

  const describe = (change: FundSettingsChangeDtoOut) =>
    t(`fundSettingsHistory.kinds.${change.kind}`, {
      name: change.targetName || t('fundSettingsRepresentativesPage.unnamed'),
      previous: change.previousValue ?? '',
      permission: permissionLabel(change.newValue),
      from: permissionLabel(change.previousValue),
      to: permissionLabel(change.newValue),
    });

  /** The office acts through its own accounts; a coordinator reads those as «המשרד». */
  const actor = (change: FundSettingsChangeDtoOut) => {
    const officeKinds = ['FounderRemovalApproved', 'FounderRemovalRejected', 'InitialRepresentativeDesignated', 'CoordinatorAdded', 'CoordinatorRemoved', 'RepresentativeDetailsUpdated'];
    if (officeKinds.includes(change.kind)) {
      return change.changedByName ? t('fundSettingsHistory.officeBy', { name: change.changedByName }) : t('fundSettingsHistory.office');
    }
    return change.changedByName ?? change.changedByEmail ?? '';
  };

  const count = changes?.length ?? 0;
  const label = isLoading
    ? t('fundSettingsHistory.title')
    : count === 0
      ? t('fundSettingsHistory.titleEmpty')
      : t('fundSettingsHistory.titleWithCount', { count, date: formatDateLocale(changes![0].changedAt) });

  return (
    <div className={styles.historyBlock}>
      <button type="button" className={styles.linkBtn} onClick={() => setIsOpen((open) => !open)} aria-expanded={isOpen}>
        {label}
        <img src={arrowDownIcon} alt="" className={`${styles.actionIcon} ${styles.historyArrow} ${isOpen ? styles.historyArrowOpen : ''}`.trim()} />
      </button>

      {isOpen && (
        count === 0 ? (
          <p className={styles.panelSubtitle}>{t('fundSettingsHistory.empty')}</p>
        ) : (
          <ul className={styles.historyList}>
            {changes!.map((change) => (
              <li key={change.id} className={styles.historyRow}>
                <span className={styles.historyDate}>{formatDateTimeLocale(change.changedAt)}</span>
                <span className={styles.historyActor} title={change.changedByEmail ?? undefined}>{actor(change)}</span>
                <span>{describe(change)}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};

export default SettingsHistorySection;
